import React, { useRef, useState } from 'react';
import { Box, Center, Heading, Image, Text, useColorModeValue, useToast, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import noWifiConnectionImg from '../../assets/images/globe-lines-arrows-slash.svg';
import { selectDeviceSetupState } from '../../features/ev-charger/device-setup/deviceSetupSlice';
import { OutlineGradientIcon } from '../../styles/custom-icons';
import { waitForReconnectGate } from '../../utils/common/wifi-reconnect-utils';
import { COMPONENT_MIN_HEIGHT, TOP_NAV_SPACING_AFFORDANCE } from '../constants';
import { ArcButton } from './ArcButton';
import { SlidingAnimationPageBase } from './SlidingAnimationPageBase';

type Props = {
  readonly onRetry: () => void | string | Promise<void | string>;
  readonly pageTitle?: string;
  readonly retryButtonTestId?: string;
  readonly titleTestId?: string;
  readonly onRetryingChange?: (isRetrying: boolean) => void;
};

export function InternetConnectionRecoveryPage({
  onRetry,
  pageTitle,
  retryButtonTestId = 'internet-connection-retry-button',
  titleTestId = 'internet-connection-title',
  onRetryingChange,
}: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast({ isClosable: true });
  const retryInFlightRef = useRef(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const { qrCodeValues } = useSelector(selectDeviceSetupState);
  const redIconColor = useColorModeValue('#FF3E24', '#FF5B44');

  async function handleRetry() {
    if (retryInFlightRef.current) {
      return;
    }

    retryInFlightRef.current = true;
    setIsRetrying(true);
    onRetryingChange?.(true);

    try {
      const canContinue = await waitForReconnectGate(qrCodeValues?.wifi?.ssid);
      if (!canContinue) {
        toast({
          title: t('EVSE.retry unsuccessful title'),
          description: t('EVSE.retry unsuccessful description'),
          status: 'warning',
          isClosable: true,
        });
        return;
      }

      const navigationTarget = await onRetry();

      if (navigationTarget) {
        navigate(navigationTarget, { replace: true });
      }
    } finally {
      retryInFlightRef.current = false;
      setIsRetrying(false);
      onRetryingChange?.(false);
    }
  }

  return (
    <SlidingAnimationPageBase px={4} title={pageTitle ?? t('EVSE.internet connection recovery title')}>
      <Box display="flex" flexDirection="column" h={`calc(${COMPONENT_MIN_HEIGHT} - ${TOP_NAV_SPACING_AFFORDANCE})`}>
        <Center pt={4} pb={2}>
          <Box position="relative" w="48vw" h="48vw">
            <OutlineGradientIcon w="100%" h="100%" color={redIconColor} data-testid="outline-icon" />
            <Image
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              w="87px"
              h="74px"
              data-testid="internet-connection-image"
              src={noWifiConnectionImg}
              alt={t('EVSE.internet connection recovery title')}
            />
          </Box>
        </Center>

        <VStack align="stretch" spacing={2} mb={2}>
          <Heading fontSize={24} fontWeight={600} textAlign="center" data-testid={titleTestId}>
            {t('EVSE.internet connection recovery title')}
          </Heading>
        </VStack>

        <VStack align="stretch" spacing={2}>
          <Text textAlign="center">{t('EVSE.internet connection recovery description primary')}</Text>
          <Text textAlign="center">{t('EVSE.internet connection recovery description secondary')}</Text>
        </VStack>

        <Center mt="auto" mb={5} flexDir={'column'}>
          <ArcButton
            data-testid={retryButtonTestId}
            onClick={handleRetry}
            isDisabled={isRetrying}
            isLoading={isRetrying}
            w="100%"
            py={6}
            mt="auto"
            arcColor="#3DCD57"
          >
            {t('Common.try again')}
          </ArcButton>
        </Center>
      </Box>
    </SlidingAnimationPageBase>
  );
}
